import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { API_BASE_URL } from '../config/api';

interface RequestOptions extends AxiosRequestConfig {
  requiresAuth?: boolean;
}

class ApiService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para agregar el token automáticamente
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para manejar errores
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const requestUrl = error.config?.url || '';
        // Solo redirigir al login si el 401 NO viene del propio endpoint de login
        if (error.response?.status === 401 && !requestUrl.includes('/auth/login')) {
          this.removeAuthToken();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { requiresAuth = true, ...restOptions } = options;

    try {
      const config: AxiosRequestConfig = {
        url: endpoint,
        ...restOptions,
      };

      // Si no requiere autenticación, eliminar el header temporalmente
      if (!requiresAuth) {
        config.headers = {
          ...config.headers,
        };
      }

      const response = await this.axiosInstance.request<T>(config);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message || 'Error en la petición';
        throw new Error(message);
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  async get<T>(endpoint: string, requiresAuth = true, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', requiresAuth, ...config });
  }

  async post<T>(
    endpoint: string,
    data?: any,
    requiresAuth = true,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      data,
      requiresAuth,
      ...config,
    });
  }

  async put<T>(
    endpoint: string,
    data?: any,
    requiresAuth = true
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      data,
      requiresAuth,
    });
  }

  async delete<T>(endpoint: string, requiresAuth = true): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', requiresAuth });
  }

  async getBlob(endpoint: string, requiresAuth = true, config?: AxiosRequestConfig): Promise<Blob> {
    const requestConfig: AxiosRequestConfig = {
      url: endpoint,
      method: 'GET',
      responseType: 'blob',
      ...config,
    };

    if (requiresAuth) {
      const token = this.getAuthToken();
      if (token) {
        requestConfig.headers = { ...requestConfig.headers, Authorization: `Bearer ${token}` };
      }
    }

    try {
      const response = await this.axiosInstance.request(requestConfig);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message || 'Error al descargar archivo';
        throw new Error(message);
      }
      throw new Error('Error de conexión con el servidor');
    }
  }

  setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  removeAuthToken(): void {
    localStorage.removeItem('auth_token');
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }
}

export const apiService = new ApiService();
